import type { UniqueIdentifier } from '@dnd-kit/core';
import type {
  SortableTreeBaseContainer,
  SortableTreeBaseItem,
  SortableTreeContainer,
  SortableTreeDrop,
  SortableTreeDropVerdict,
  SortableTreeNode,
} from '@payaca/dnd';

import {
  ProposalItemGroupSectionQuery,
  ProposalSectionsQuery,
} from '@/api/queries/proposal/useGetProposal';

type QueryGroupChild =
  ProposalItemGroupSectionQuery['groups'][number]['children'][number];

/** A Line Item's fields, exactly as the query fetches them. */
type ProposalGroupLineItemSource = Extract<
  QueryGroupChild,
  { __typename: 'ProposalGroupLineItem' }
>;

/** A group's own fields, exactly as the query fetches them, minus its `children`. */
type ProposalItemGroupSource = Omit<
  Extract<QueryGroupChild, { __typename: 'ProposalItemGroup' }>,
  'children'
>;

/** A Line Item tree node: the base node plus the fetched Line Item data. */
export type ProposalItem = SortableTreeBaseItem & ProposalGroupLineItemSource;

/**
 * The group fields are partial because the deepest level the query unrolls to
 * emits a fieldless `{ __typename: 'ProposalItemGroup' }` stub, which the server's
 * nesting cap (see `groupTree.ts`) means never carries data at runtime.
 */
export type ProposalContainer = SortableTreeBaseContainer &
  Partial<ProposalItemGroupSource> & {
    hasMultiChoiceSubGroup: boolean;
    hasOptionalSubGroup: boolean;
  };

export type ProposalNode = SortableTreeNode<ProposalItem, ProposalContainer>;
export type ProposalContainerNode = SortableTreeContainer<
  ProposalItem,
  ProposalContainer
>;

/**
 * Field types are derived from the query, so dropping a fetched field fails to
 * compile. The recursion is hand-assembled, since GraphQL only unrolls the
 * schema's recursive `ProposalItemGroupChild` union to a fixed depth.
 */
type ProposalGroupSource = Partial<ProposalItemGroupSource> & {
  __typename: 'ProposalItemGroup';
  children?: ProposalGroupChildSource[];
};

type ProposalGroupChildSource =
  | ProposalGroupSource
  | ProposalGroupLineItemSource;

/** Converts a Proposal item group section into a {@link SortableTree} items prop. */
export const proposalItemGroupSectionToTree = (
  section: ProposalItemGroupSectionQuery
): ProposalContainerNode[] =>
  section.groups
    .map(groupToContainerNode)
    .filter((node): node is ProposalContainerNode => node !== null);

const groupToContainerNode = (
  group: ProposalGroupSource
): ProposalContainerNode | null => {
  // Drop truncated stub groups: at the query's deepest level only line items are
  // selected, so a group there deserialises with no key and would render a
  // colliding empty id. The server's nesting cap means it never happens at
  // runtime.
  if (group.key == null) return null;
  const { children, ...groupFields } = group;

  const subGroupChildren =
    group.children?.filter(
      (child) => child.__typename === 'ProposalItemGroup'
    ) || [];

  return {
    ...groupFields,
    type: 'container',
    id: group.key,
    children: (children ?? [])
      .map(groupChildToNode)
      .filter((node): node is ProposalNode => node !== null),
    hasMultiChoiceSubGroup:
      subGroupChildren.filter((i) => i.selectionMode === 'MULTI_CHOICE')
        .length > 0,
    hasOptionalSubGroup:
      subGroupChildren.filter((i) => i.selectionMode === 'OPTIONAL').length > 0,
  };
};

const groupChildToNode = (
  child: ProposalGroupChildSource
): ProposalNode | null =>
  child.__typename === 'ProposalItemGroup'
    ? groupToContainerNode(child)
    : // Key item nodes by publicId: it is stable across the optimistic insert
      // and the reconciling refetch, where the numeric id is not.
      { ...child, type: 'item', id: child.proposalLineItem.publicId };

const containsLineItem = (children: ProposalGroupChildSource[]): boolean =>
  children.some((child) =>
    child.__typename === 'ProposalItemGroup'
      ? containsLineItem(child.children ?? [])
      : true
  );

/**
 * True when a section has nothing worth drawing a group bar for: no groups at
 * all (a Proposal converted from an Estimate), or only the single untitled,
 * empty group a fresh Proposal is seeded with. A titled group, or a second
 * group, keeps its bar even with no items in it, so emptying a group never
 * hides one the Sales Agent made.
 */
export const proposalTreeIsUnstarted = (
  nodes: ProposalContainerNode[]
): boolean => {
  if (nodes.length === 0) return true;
  if (nodes.length > 1) return false;
  const [only] = nodes;
  return !only.title && only.children.length === 0;
};

/** True when any of a Proposal's item group sections holds at least one Line Item. */
export const proposalSectionsHaveLineItem = (
  sections: ProposalSectionsQuery
): boolean =>
  sections.some(
    (section) =>
      section.__typename === 'ProposalItemGroupSection' &&
      containsLineItem(section.groups)
  );

type ProposalDrop = SortableTreeDrop<ProposalItem, ProposalContainer>;
export const canDropProposalNode = (
  drop: ProposalDrop
): SortableTreeDropVerdict => {
  if (drop.active.type === 'item') {
    return drop.parentId === null
      ? { reason: 'Items need to be in a group' }
      : true;
  }

  // Removable guard: lift this to enable reparenting.
  if (drop.parentId === drop.currentParentId) {
    return true;
  }

  return drop.currentParentId === null
    ? { reason: "Groups can't go inside another group" }
    : { reason: 'Sub-groups can only move within their group' };
};

export const groupHeadingId = (id: UniqueIdentifier) => {
  return `group-heading-${id.toString()}`;
};

export const groupNameId = (id: UniqueIdentifier) => {
  return `group-name-${id.toString()}`;
};

/**
 * The first non-blank line of a Markdown string, with its syntax removed, so
 * it can stand in as a plain-text name.
 */
export const firstLineAsPlainText = (markdown: string | null | undefined) => {
  const firstLine =
    markdown?.split('\n').find((line) => line.trim() !== '') ?? '';

  return firstLine
    .replace(/^\s*(#{1,6}\s+|>\s*|[-*+]\s+|\d+[.)]\s+)/, '')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/(?<!\\)(\*\*|~~|\*|`)/g, '')
    .replace(/(?<!\\)(\b__|__\b)/g, '')
    .replace(/(?<!\\)(\b_|_\b)/g, '')
    .replace(/\\([!-/:-@[-`{-~])/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * A plain-text name for a tree node, for the drag preview and screen-reader
 * announcements. A line item without a reference falls back to its
 * description's first line, as its row does.
 */
export const proposalNodeLabel = (node: ProposalNode): string => {
  if (node.type === 'container') {
    return node.title || 'Untitled group';
  }

  const { internalReference, description } = node.proposalLineItem;
  return (
    internalReference || firstLineAsPlainText(description) || 'Untitled item'
  );
};
